/**
 * Corporate Action Form Screen
 *
 * Allows users to create corporate actions (share splits, consolidations, bonus shares).
 * Shows impact preview and warning about permanent changes.
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import type { StackNavigationProp } from '@react-navigation/stack';
import {
  CorporateActionForm,
  CorporateActionData,
} from '../../components/portfolio/CorporateActionForm';
import { showSuccess, showError, showConfirm } from '../../utils/alert';
import { useTheme } from '../../contexts/ThemeContext';
import { useCreateCorporateAction } from '../../services/api/queries/useTrades';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

type CorporateActionFormScreenNavigationProp = StackNavigationProp<
  PortfolioNavigatorParamList,
  'CorporateAction'
>;
type CorporateActionFormScreenRouteProp = RouteProp<PortfolioNavigatorParamList, 'CorporateAction'>;

export function CorporateActionFormScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<CorporateActionFormScreenNavigationProp>();
  const route = useRoute<CorporateActionFormScreenRouteProp>();

  // Get positionId and symbol from route params (e.g., from position detail screen)
  const positionIdParam = route.params?.positionId;
  const symbolParam = route.params?.symbol;
  const instrumentNameParam = route.params?.instrumentName;

  const createCorporateActionMutation = useCreateCorporateAction();

  const handleSubmit = async (data: CorporateActionData) => {
    // Show confirmation before creating corporate action
    const confirmed = await showConfirm(
      t('portfolio.confirmCorporateAction', { defaultValue: 'Confirm Corporate Action' }),
      t('portfolio.caConfirmMessage', {
        defaultValue:
          '⚠️ This will permanently update all trades BEFORE {{date}}. Trades on or after this date will NOT be affected. This action CANNOT be undone. Continue?',
        date: data.exclude_date,
      }),
      {
        confirmText: t('common.confirm', { defaultValue: 'Confirm' }),
        cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
        destructiveAction: true,
      }
    );

    if (!confirmed) return;

    try {
      await createCorporateActionMutation.mutateAsync(data);

      showSuccess(
        t('portfolio.corporateActionCreated', {
          defaultValue: '{{type}} corporate action created',
          type: getActionTypeLabel(data.action_type),
        })
      );
      navigation.goBack();
    } catch (error: any) {
      showError(
        error.message ||
          t('portfolio.failedToCreateCorporateAction', {
            defaultValue: 'Failed to create corporate action',
          })
      );
    }
  };

  const getActionTypeLabel = (type: string): string => {
    switch (type) {
      case 'split':
        return t('portfolio.shareSplit', { defaultValue: 'Share Split' });
      case 'consolidate':
        return t('portfolio.consolidation', { defaultValue: 'Share Consolidation' });
      case 'bonus':
        return t('portfolio.bonusShares', { defaultValue: 'Bonus Shares' });
      default:
        return type;
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <CorporateActionForm
          onSubmit={handleSubmit}
          onCancel={() => navigation.goBack()}
          loading={createCorporateActionMutation.isLoading}
          positionId={positionIdParam}
          symbol={symbolParam}
          readOnlySymbol={!!symbolParam}
          instrumentName={instrumentNameParam}
        />
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollContent: {
      padding: 16,
    },
    header: {
      marginBottom: 24,
    },
    title: {
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: theme.typography.fontSizeMd,
    },
  });
}
